/**
 * Contextual paywall.
 *
 * Rules this component follows:
 *   • Prices, currencies and billing periods come from RevenueCat. Nothing is
 *     hardcoded.
 *   • Trial wording appears ONLY when the store says this customer is eligible
 *     for that package's trial. Otherwise the button reads "Subscribe".
 *   • The dialog is always dismissible and always offers to continue on Free.
 *     No countdowns, no dark patterns.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PAYWALL_CONTEXT, PLAN_LIMITS, TRIAL_DAYS } from '@chordbookpro/shared';
import { config } from '../../config/appConfig.js';
import { useUiStore } from '../../stores/uiStore.js';
import { useAuth } from '../../providers/AuthProvider.jsx';
import { useSubscription } from '../../providers/SubscriptionProvider.jsx';
import { Modal } from '../ui/Modal.jsx';
import { Button } from '../ui/Button.jsx';
import { Spinner } from '../ui/Spinner.jsx';
import * as subscriptionService from '../../services/subscriptions/subscriptionService.js';
import { isBillingConfigured } from '../../config/appConfig.js';
import styles from './PaywallDialog.module.css';

/** Copy per contextual variant. Explains the limit; never shames the user. */
const CONTEXT_COPY = {
  [PAYWALL_CONTEXT.GENERAL_UPGRADE]: {
    title: 'Upgrade to Premium',
    body: 'Unlimited playlists, unlimited songs, offline access and the full editor.',
  },
  [PAYWALL_CONTEXT.PLAYLIST_LIMIT]: {
    title: 'You have used your Free playlist',
    body: `Your Free plan supports ${PLAN_LIMITS.free.playlists} playlist with up to ${PLAN_LIMITS.free.songsPerPlaylist} songs. Upgrade to Premium for unlimited playlists and songs.`,
  },
  [PAYWALL_CONTEXT.PLAYLIST_SONG_LIMIT]: {
    title: 'This playlist is full',
    body: `Your Free plan supports one playlist with up to ${PLAN_LIMITS.free.songsPerPlaylist} songs. Upgrade to Premium for unlimited playlists and songs.`,
  },
  [PAYWALL_CONTEXT.CUSTOM_SONG_LIMIT]: {
    title: 'All your song slots are in use',
    body: `You have used all ${PLAN_LIMITS.free.customSongs} custom-song slots included with the Free plan. Upgrade to Premium to create unlimited songs and arrangements.`,
  },
  [PAYWALL_CONTEXT.OFFLINE_REQUIRED]: {
    title: 'Offline catalog is a Premium feature',
    body: 'Premium downloads catalog songs for venues with no signal. Songs you created yourself are always available offline, on any plan.',
  },
  [PAYWALL_CONTEXT.ADVANCED_EDITOR_REQUIRED]: {
    title: 'Advanced editor tools are in Premium',
    body: 'ChordPro import and export, the plain-text import assistant and bulk editing come with Premium.',
  },
  [PAYWALL_CONTEXT.REVISION_HISTORY_REQUIRED]: {
    title: 'Revision history is a Premium feature',
    body: 'Premium keeps earlier versions of your songs so you can compare them and restore one.',
  },
  [PAYWALL_CONTEXT.LEARN_DAILY_LIMIT]: {
    title: 'That is today’s free lessons done',
    body: `The Free plan includes ${PLAN_LIMITS.free.learnLessonsPerDay} new lessons a day. Come back tomorrow, or upgrade to Premium to keep going for as long as you like. Revising lessons you have already finished is always free, and so is the daily challenge.`,
  },
  [PAYWALL_CONTEXT.LEARN_UNIT_LOCKED]: {
    title: 'This unit is part of Premium',
    body: 'Premium opens the complete curriculum and removes the daily lesson limit. Everything you have already learned stays yours.',
  },
  [PAYWALL_CONTEXT.TUNER_TUNING_LOCKED]: {
    title: 'Alternate tunings are part of Premium',
    body: 'Standard six-string tuning is free, with both the microphone and the reference notes. Premium adds the other nineteen presets, seven- and eight-string guitars, and setting each string yourself. Nothing you can already do is taken away.',
  },
};

const PRO_BENEFITS = [
  'Unlimited playlists and setlists',
  'Unlimited songs per playlist',
  'Unlimited songs of your own',
  'Unlimited lessons and the full curriculum',
  'Offline access to the catalog',
  'Every tuning in the tuner, including 7- and 8-string',
  'ChordPro import and export',
  'Revision history',
];

export function PaywallDialog() {
  const { open, context, details } = useUiStore((state) => state.paywall);
  const openPaywall = useUiStore((state) => state.openPaywall);
  const closePaywall = useUiStore((state) => state.closePaywall);
  const showToast = useUiStore((state) => state.showToast);

  const { firebaseUser, isAuthenticated } = useAuth();
  const { isPro, refetch } = useSubscription();

  const [selectedId, setSelectedId] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  const offeringsQuery = useQuery({
    queryKey: ['billing', 'offerings'],
    queryFn: () => subscriptionService.loadOfferings(),
    enabled: open && isAuthenticated && isBillingConfigured,
    staleTime: 5 * 60_000,
  });

  const packages = offeringsQuery.data?.packages ?? [];

  // Default to annual — it is the better value, and pre-selecting it is
  // helpful rather than manipulative because both options stay visible.
  useEffect(() => {
    if (!selectedId && packages.length > 0) {
      const annual = packages.find((item) => item.period === 'annual');
      setSelectedId((annual ?? packages[0]).id);
    }
  }, [packages, selectedId]);

  const selected = packages.find((item) => item.id === selectedId) ?? null;
  const copy = CONTEXT_COPY[context] ?? CONTEXT_COPY[PAYWALL_CONTEXT.GENERAL_UPGRADE];

  async function handlePurchase() {
    if (!selected) return;

    setPurchasing(true);
    setPurchaseError(null);

    /*
     * Stand this dialog down before handing off to RevenueCat.
     *
     * Modal renders a native <dialog> opened with showModal(), which the
     * browser promotes to the top layer and which makes everything outside it
     * inert. RevenueCat's checkout mounts into ordinary DOM, so leaving this
     * open paints our spinner over a payment form that cannot be clicked or
     * typed into — the purchase promise then never settles.
     */
    closePaywall();

    try {
      await subscriptionService.purchase(selected, { email: firebaseUser?.email });
      await refetch();

      showToast({ message: 'Welcome to Premium. Everything is unlocked.', tone: 'success' });
    } catch (error) {
      // Cancelling checkout is a choice, not a failure — say nothing.
      if (error.cancelled) return;

      // Reopen on the same context so the error is not lost with the dialog.
      setPurchaseError(error.message);
      openPaywall(context, details);
    } finally {
      setPurchasing(false);
    }
  }

  if (isPro && open) {
    return (
      <Modal open={open} onClose={closePaywall} title="You already have Premium" size="sm">
        <p>Everything is unlocked. Manage your subscription from the billing page.</p>
        <div className={styles.actions}>
          <Button to="/app/billing" variant="secondary" onClick={closePaywall}>
            Manage subscription
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={closePaywall} title={copy.title} size="md">
      <p className={styles.body}>{copy.body}</p>

      {/* Show the exact numbers when a quota triggered this. */}
      {details?.limit !== undefined && details?.current !== undefined && (
        <p className={styles.usage}>
          Using {details.current} of {details.limit} on the Free plan.
        </p>
      )}

      <ul className={styles.benefits}>
        {PRO_BENEFITS.map((benefit) => (
          <li key={benefit} className={styles.benefit}>
            <CheckIcon /> {benefit}
          </li>
        ))}
      </ul>

      {!isAuthenticated && (
        <p className={styles.notice}>
          <Button to="/register" variant="primary" fullWidth onClick={closePaywall}>
            Create an account to subscribe
          </Button>
        </p>
      )}

      {/* Addressed to the person reading it, not to the operator who has to fix
          it — the misconfiguration is ours, and they can still subscribe. */}
      {isAuthenticated && !isBillingConfigured && (
        <p className={styles.notice}>
          Subscriptions cannot be purchased on the web just now. You can subscribe in the{' '}
          {config.appName} app for iPhone or Android, and Premium will apply to your account
          everywhere.
        </p>
      )}

      {isAuthenticated && isBillingConfigured && (
        <>
          {offeringsQuery.isLoading && (
            <div className={styles.loading}>
              <Spinner size={24} label="Loading subscription options" />
              <span>Loading prices…</span>
            </div>
          )}

          {offeringsQuery.isError && (
            <p className={styles.error}>
              We could not load subscription options. Check your connection and try again.
            </p>
          )}

          {!offeringsQuery.isLoading && packages.length === 0 && !offeringsQuery.isError && (
            <p className={styles.notice}>
              No subscription options are available right now. Please try again shortly.
            </p>
          )}

          {packages.length > 0 && (
            <>
              <div className={styles.packages} role="radiogroup" aria-label="Subscription options">
                {packages.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={item.id === selectedId}
                    className={`${styles.package} ${item.id === selectedId ? styles.packageSelected : ''}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <span className={styles.packagePeriod}>
                      {item.period === 'annual' ? 'Annual' : item.period === 'monthly' ? 'Monthly' : item.title}
                    </span>

                    {/* Localized price string, exactly as the store returned it. */}
                    <span className={styles.packagePrice}>{item.priceFormatted}</span>

                    {item.trial.available && (
                      <span className={styles.packageTrial}>{item.trial.periodLabel}</span>
                    )}
                  </button>
                ))}
              </div>

              {purchaseError && (
                <p className={styles.error} role="alert">
                  {purchaseError}
                </p>
              )}

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handlePurchase}
                loading={purchasing}
                disabled={!selected}
              >
                {/* Trial wording only when this package is genuinely eligible. */}
                {selected?.trial.available
                  ? `Start ${selected.trial.periodLabel}`
                  : 'Subscribe to Premium'}
              </Button>

              {/* Mandatory auto-renewal disclosure. */}
              {selected && (
                <p className={styles.disclosure}>
                  {selected.trial.available ? (
                    <>
                      Your {selected.trial.periodLabel} starts today. After it ends you will be
                      charged {selected.priceFormatted}
                      {selected.period === 'annual' ? ' each year' : ' each month'} unless you
                      cancel first.
                    </>
                  ) : (
                    <>
                      You will be charged {selected.priceFormatted}
                      {selected.period === 'annual' ? ' each year' : ' each month'}.
                    </>
                  )}{' '}
                  This subscription renews automatically until cancelled. Cancel any time from the
                  billing page in your account.
                </p>
              )}
            </>
          )}
        </>
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.continueFree} onClick={closePaywall}>
          Continue with the Free plan
        </button>

        <p className={styles.legal}>
          <a href="/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          {' · '}
          <a href={`mailto:${config.supportEmail}`}>Support</a>
        </p>

        <p className={styles.legal}>
          Purchases made in the iPhone or Android app are managed there. New subscribers may be
          eligible for a {TRIAL_DAYS}-day trial, as determined by the store.
        </p>
      </div>
    </Modal>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="m3.5 8.5 3 3 6-7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
